import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { Icon } from './Icon';

/** The AI's recommendation — rendered as the chip's coloured BORDER. `null` = no recommendation (e.g. an ambiguous Selection Decision). */
export type IncludeRecommendation = 'include' | 'exclude' | null;
/** The designer's Inclusion Decision — rendered as the chip's pale semantic FILL. */
export type IncludeSelection = 'included' | 'excluded' | 'undecided';

export type IncludeExcludeControlProps = {
  /**
   * What the AI recommends. While the decision is `undecided`, the
   * recommended side's chip gets the matching accent **border** (green
   * include / red exclude). Once the designer picks a side the border
   * disappears (the choice is what matters now); it returns after a
   * deselect. `null` shows no border.
   */
  recommendation: IncludeRecommendation;
  /**
   * The designer's actual decision. The chosen side **fills** with the pale
   * semantic tint; re-clicking the filled side fires `onClear` (deselect →
   * undecided), restoring the recommendation border.
   */
  selection: IncludeSelection;
  /** `compact` = icon-only chip pair for table rows; `full` = labelled chips for the detail panel. */
  size?: 'compact' | 'full';
  /** Compact chip dimensions: `md` (32px, subsection rows) or `sm` (24px, test-case rows). */
  compactSize?: 'sm' | 'md';
  onInclude: () => void;
  onExclude: () => void;
  /** Fires when the currently-filled side is clicked again (deselect). */
  onClear: () => void;
  /** Visible label on the include chip (`full` only). */
  includeLabel?: string;
  /** Visible label on the exclude chip (`full` only). */
  excludeLabel?: string;
  /** Accessible-name suffix for the `compact` chips, e.g. a Test ID → "Include UAR-01". */
  ariaSuffix?: string;
  /** `full` only: when the case is included, the timestamp shown on the
   *  "Test Case Included" confirmation pill (e.g. "15:31 | 12.01.2026"). */
  decidedAt?: string;
  /**
   * Render the resting chips on white instead of the soft grey fill — used when
   * the control sits on an already-grey "active" surface (an expanded subsection
   * row) so the +/− buttons stay legible.
   */
  activeSurface?: boolean;
};

/**
 * Per-state chip colouring (Figma "Include/Exclude", node 1061:101032): chips
 * rest on the soft grey fill; the AI recommendation draws the accent BORDER;
 * the designer's selection swaps the fill for the pale semantic tint. Exactly
 * one bg + one border utility per state — stacking same-property utilities
 * resolves by stylesheet order, not class order (the bug that ate the fill on
 * the old table toggle).
 */
const chip = (
  side: 'include' | 'exclude',
  selected: boolean,
  recommended: boolean,
  activeSurface = false,
): string => {
  // On an "active" surface (e.g. an expanded subsection row, which tints grey)
  // the resting chip fill flips to white so the +/− buttons stay legible.
  const rest = activeSurface ? 'bg-white' : 'bg-ink-100';
  return side === 'exclude'
    ? selected
      ? 'border-danger-accent bg-danger-bg'
      : recommended
        ? `border-danger-accent ${rest}`
        : `border-transparent ${rest}`
    : selected
      ? 'border-success-accent bg-success-bg'
      : recommended
        ? `border-success-accent ${rest}`
        : `border-transparent ${rest}`;
};

const CHIP_BASE = clsx(
  'inline-flex cursor-pointer items-center justify-center rounded-sm border',
  'transition-colors duration-fast hover:brightness-[0.97]',
);

/**
 * The canonical include/exclude control (the "border = AI recommendation, fill
 * = the designer's choice, click-the-fill-again = deselect" affordance). Used
 * by the detail-page InclusionPanel (`full`), the Assembly TestCaseTable rows
 * and the SubsectionRow (`compact`) so the behaviour lives in ONE place.
 * Presentational: the three callbacks are the only side effects, and
 * `aria-pressed` tracks the SELECTION (not the recommendation border). Clicks
 * stop propagation so a chip press never triggers an enclosing row's
 * navigation. Glyphs stay permanently coloured (red −, green +) per Figma.
 */
export function IncludeExcludeControl({
  recommendation,
  selection,
  size = 'full',
  compactSize = 'md',
  onInclude,
  onExclude,
  onClear,
  includeLabel = 'Approve & Include',
  excludeLabel = 'Exclude Test Case',
  ariaSuffix,
  decidedAt,
  activeSurface = false,
}: IncludeExcludeControlProps) {
  const includeSelected = selection === 'included';
  const excludeSelected = selection === 'excluded';
  // The recommendation is advisory: it shows only while UNDECIDED. Once a side
  // is chosen the border disappears (and returns after a deselect).
  const showRecommendation = selection === 'undecided';
  const includeRecommended = showRecommendation && recommendation === 'include';
  const excludeRecommended = showRecommendation && recommendation === 'exclude';

  // Clicking the filled side clears it (deselect); an unfilled side sets it.
  const handleInclude = (e: MouseEvent) => {
    e.stopPropagation();
    (includeSelected ? onClear : onInclude)();
  };
  const handleExclude = (e: MouseEvent) => {
    e.stopPropagation();
    (excludeSelected ? onClear : onExclude)();
  };

  const excludeTitle = excludeRecommended ? 'AI recommends excluding this test case' : undefined;
  const includeTitle = includeRecommended ? 'AI recommends including this test case' : undefined;

  if (size === 'compact') {
    const suffix = ariaSuffix ?? 'test case';
    // md = 32px (subsection rows); sm = 24px (test-case rows).
    const chipDim = compactSize === 'sm' ? 'h-6 w-6' : 'h-8 w-8';
    return (
      <div
        role="group"
        aria-label={`Include or exclude ${suffix}`}
        className="inline-flex items-center gap-3"
      >
        <button
          type="button"
          aria-label={`Exclude ${suffix}`}
          aria-pressed={excludeSelected}
          {...(excludeTitle ? { title: excludeTitle } : {})}
          data-side="exclude"
          data-recommended={excludeRecommended}
          onClick={handleExclude}
          className={clsx(
            CHIP_BASE,
            chipDim,
            chip('exclude', excludeSelected, excludeRecommended, activeSurface),
          )}
        >
          <Icon name="minus" size={14} className="text-danger-accent" />
        </button>
        <button
          type="button"
          aria-label={`Include ${suffix}`}
          aria-pressed={includeSelected}
          {...(includeTitle ? { title: includeTitle } : {})}
          data-side="include"
          data-recommended={includeRecommended}
          onClick={handleInclude}
          className={clsx(
            CHIP_BASE,
            chipDim,
            chip('include', includeSelected, includeRecommended, activeSurface),
          )}
        >
          <Icon name="plus" size={14} className="text-success-accent" />
        </button>
      </div>
    );
  }

  if (includeSelected) {
    // Figma 1061-105013 — once INCLUDED, the chooser collapses to a green
    // confirmation pill: "Test Case Included" + a pencil to re-open the choice
    // (clears the decision), and the decision timestamp on the right.
    return (
      <div className="flex h-9 items-center justify-between rounded-[4px] bg-[#1d7c5f]/[0.15] px-3 py-2">
        <button
          type="button"
          aria-label="Edit inclusion decision"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[12px] font-medium leading-none text-[#1d7c5f]"
        >
          Test Case Included
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className="block shrink-0"
          >
            <path
              d="M17.7594 5.73203L14.268 2.24141C14.1519 2.1253 14.0141 2.0332 13.8624 1.97037C13.7107 1.90753 13.5482 1.87519 13.384 1.87519C13.2198 1.87519 13.0572 1.90753 12.9056 1.97037C12.7539 2.0332 12.6161 2.1253 12.5 2.24141L2.86641 11.875C2.74983 11.9907 2.65741 12.1283 2.59451 12.28C2.5316 12.4317 2.49948 12.5944 2.50001 12.7586V16.25C2.50001 16.5815 2.6317 16.8995 2.86612 17.1339C3.10054 17.3683 3.41849 17.5 3.75001 17.5H7.24141C7.40563 17.5005 7.5683 17.4684 7.72003 17.4055C7.87176 17.3426 8.00938 17.2502 8.12501 17.1336L17.7594 7.5C17.8755 7.38392 17.9676 7.24611 18.0304 7.09444C18.0933 6.94276 18.1256 6.78019 18.1256 6.61602C18.1256 6.45184 18.0933 6.28927 18.0304 6.1376C17.9676 5.98592 17.8755 5.84811 17.7594 5.73203ZM7.24141 16.25H3.75001V12.7586L11.25 5.2586L14.7414 8.75L7.24141 16.25ZM15.625 7.86641L12.1336 4.375L13.8836 2.625L17.375 6.11641L15.625 7.86641Z"
              fill="#1d7c5f"
            />
          </svg>
        </button>
        {decidedAt ? (
          <span className="text-[10px] font-medium leading-[1.4] text-[#1d7c5f]">
            Included at {decidedAt}
          </span>
        ) : null}
      </div>
    );
  }

  if (excludeSelected) {
    // Figma 1061-105286 — the EXCLUDED counterpart of the included pill: a red
    // (rgba(217,68,51,0.1)) confirmation pill with "Test Case Excluded" + a
    // pencil to re-open the choice, and the decision timestamp on the right.
    return (
      <div className="flex h-9 items-center justify-between rounded-[4px] bg-[#d94433]/[0.1] px-3 py-2">
        <button
          type="button"
          aria-label="Edit inclusion decision"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[12px] font-medium leading-none text-[#d94433]"
        >
          Test Case Excluded
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className="block shrink-0"
          >
            <path
              d="M17.7594 5.73203L14.268 2.24141C14.1519 2.1253 14.0141 2.0332 13.8624 1.97037C13.7107 1.90753 13.5482 1.87519 13.384 1.87519C13.2198 1.87519 13.0572 1.90753 12.9056 1.97037C12.7539 2.0332 12.6161 2.1253 12.5 2.24141L2.86641 11.875C2.74983 11.9907 2.65741 12.1283 2.59451 12.28C2.5316 12.4317 2.49948 12.5944 2.50001 12.7586V16.25C2.50001 16.5815 2.6317 16.8995 2.86612 17.1339C3.10054 17.3683 3.41849 17.5 3.75001 17.5H7.24141C7.40563 17.5005 7.5683 17.4684 7.72003 17.4055C7.87176 17.3426 8.00938 17.2502 8.12501 17.1336L17.7594 7.5C17.8755 7.38392 17.9676 7.24611 18.0304 7.09444C18.0933 6.94276 18.1256 6.78019 18.1256 6.61602C18.1256 6.45184 18.0933 6.28927 18.0304 6.1376C17.9676 5.98592 17.8755 5.84811 17.7594 5.73203ZM7.24141 16.25H3.75001V12.7586L11.25 5.2586L14.7414 8.75L7.24141 16.25ZM15.625 7.86641L12.1336 4.375L13.8836 2.625L17.375 6.11641L15.625 7.86641Z"
              fill="#d94433"
            />
          </svg>
        </button>
        {decidedAt ? (
          <span className="text-[10px] font-medium leading-[1.4] text-[#d94433]">
            Excluded at {decidedAt}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    // Figma uat-05..07: two 166x40 chips side-by-side with 8px gap.
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        aria-pressed={excludeSelected}
        {...(excludeTitle ? { title: excludeTitle } : {})}
        data-side="exclude"
        data-recommended={excludeRecommended}
        onClick={handleExclude}
        className={clsx(
          CHIP_BASE,
          'h-10 gap-[10px] pl-[6px] pr-3 text-center text-[12px] font-medium leading-[18px] text-[#0e122d]',
          chip('exclude', excludeSelected, excludeRecommended, activeSurface),
        )}
      >
        {/* Figma: the glyph sits in a 35×28 white box. */}
        <span className="flex h-[28px] w-[31.5px] flex-shrink-0 items-center justify-center rounded-[6px] bg-white py-2">
          <Icon name="minus" size={16} className="text-danger-accent" />
        </span>
        <span>{excludeLabel}</span>
      </button>
      <button
        type="button"
        aria-pressed={includeSelected}
        {...(includeTitle ? { title: includeTitle } : {})}
        data-side="include"
        data-recommended={includeRecommended}
        onClick={handleInclude}
        className={clsx(
          CHIP_BASE,
          'h-10 gap-[10px] pl-[6px] pr-3 text-center text-[12px] font-medium leading-[18px] text-[#0e122d]',
          chip('include', includeSelected, includeRecommended, activeSurface),
        )}
      >
        {/* Figma: the glyph sits in a 35×28 white box. */}
        <span className="flex h-[28px] w-[31.5px] flex-shrink-0 items-center justify-center rounded-[6px] bg-white py-2">
          <Icon name="plus" size={16} className="text-success-accent" />
        </span>
        <span>{includeLabel}</span>
      </button>
    </div>
  );
}
